<template>
  <section class="subscribe-page">
    <div class="header-band"></div>

    <div v-if="!business.affiliate_id" class="container position-relative py-5">
      <div v-if="!showPaymentOnly" class="pricing-hero text-center mx-auto mb-5">
        <h1 class="fw-800 text-dark mb-3">{{ fromOnboarding ? 'Finalisez votre abonnement' : 'Tarifs GStocky' }}</h1>
        <p class="lead text-muted mb-0">
          {{ heroSubtitle }}
        </p>
      </div>

      <section v-if="showPaymentOnly" ref="paymentPanel" class="checkout-shell" aria-label="Finaliser l’abonnement">
        <div class="payment-workflow">
          <router-link to="/subscribe" class="checkout-back-link">
            <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
            Retour aux plans
          </router-link>

          <div class="checkout-summary" aria-label="Récapitulatif de la commande">
            <div class="checkout-plan">
              <span>Votre plan</span>
              <div>
                <strong>{{ selectedPlan.name }}</strong>
                <span class="checkout-interval">{{ selectedBillingLabel }}</span>
                <router-link to="/subscribe">Modifier</router-link>
              </div>
            </div>
            <div class="checkout-total">
              <span>À payer</span>
              <strong>{{ selectedPlanPriceLabel }}</strong>
            </div>
          </div>

          <section v-if="!isMorocco" class="payment-method-section" aria-labelledby="payment-method-title">
            <h2 id="payment-method-title">Mode de paiement</h2>
            <div class="payment-method-options" role="radiogroup" aria-label="Choisir un mode de paiement">
              <button
                type="button"
                class="payment-method-option"
                :class="{ active: paymentMethod === 'bank_transfer' }"
                role="radio"
                :aria-checked="paymentMethod === 'bank_transfer'"
                @click="selectPaymentMethod('bank_transfer')"
              >
                <span class="payment-method-icon"><AppIcon name="bi-bank" class="bi bi-bank" aria-hidden="true" /></span>
                <span><strong>Virement bancaire</strong><small>Envoyer un justificatif</small></span>
                <AppIcon v-if="paymentMethod === 'bank_transfer'" name="bi-check-circle-fill" class="bi bi-check-circle-fill payment-method-check" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="payment-method-option"
                :class="{ active: paymentMethod === 'paypal' }"
                role="radio"
                :aria-checked="paymentMethod === 'paypal'"
                @click="selectPaymentMethod('paypal')"
              >
                <span class="payment-method-icon paypal-icon">P</span>
                <span><strong>PayPal</strong><small>Paiement sécurisé immédiat</small></span>
                <AppIcon v-if="paymentMethod === 'paypal'" name="bi-check-circle-fill" class="bi bi-check-circle-fill payment-method-check" aria-hidden="true" />
              </button>
            </div>
          </section>

          <template v-if="paymentMethod === 'bank_transfer'">
            <section class="payment-block" aria-labelledby="bank-details-title">
              <h2 id="bank-details-title">RIB</h2>
              <div class="rib-box">
                <span>{{ ribNumber }}</span>
                <button type="button" @click="copyRIB" :aria-label="ribCopied ? 'RIB copié' : 'Copier le RIB'">
                  <AppIcon :name="ribCopied ? 'bi bi-check2' : 'bi bi-copy'" :class="ribCopied ? 'bi bi-check2' : 'bi bi-copy'" aria-hidden="true" />
                  {{ ribCopied ? 'Copié' : 'Copier' }}
                </button>
              </div>
            </section>

            <section class="payment-block" aria-labelledby="receipt-title">
              <h2 id="receipt-title">Reçu de virement</h2>

              <input
                id="receipt-input"
                ref="receiptInput"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                class="visually-hidden receipt-input"
                @change="handleFileSelect"
              >
              <div
                class="receipt-dropzone"
                :class="{ active: isDragging, selected: selectedFile }"
                role="button"
                tabindex="0"
                :aria-label="selectedFile ? `Justificatif sélectionné : ${selectedFile.name}` : 'Choisir un justificatif de paiement'"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
                @click="openReceiptPicker"
                @keydown.enter.prevent="openReceiptPicker"
                @keydown.space.prevent="openReceiptPicker"
              >
                <template v-if="!selectedFile">
                  <span class="upload-icon"><AppIcon name="bi-cloud-arrow-up" class="bi bi-cloud-arrow-up" aria-hidden="true" /></span>
                  <strong>Glissez-déposez votre reçu</strong>
                  <span>ou choisissez un fichier</span>
                  <small>PDF ou image · 10 Mo max</small>
                </template>
                <template v-else>
                  <span class="upload-icon file-ready"><AppIcon name="bi-file-earmark-check" class="bi bi-file-earmark-check" aria-hidden="true" /></span>
                  <div class="selected-file-copy">
                    <strong>{{ selectedFile.name }}</strong>
                    <span>{{ formatFileSize(selectedFile.size) }} · Prêt à envoyer</span>
                  </div>
                  <button type="button" class="remove-file-btn" @click.stop="clearSelectedFile" aria-label="Supprimer le justificatif">
                    <AppIcon name="bi-trash3" class="bi bi-trash3" aria-hidden="true" />
                  </button>
                </template>
              </div>
            </section>

            <p class="verification-note">
              Votre compte sera activé dès que nous aurons vérifié votre reçu.
            </p>

            <button
              type="button"
              class="submit-proof-btn"
              :disabled="paymentRequesting || !selectedFile"
              @click="uploadReceipt"
            >
              <span v-if="paymentRequesting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ paymentRequesting ? 'Envoi en cours...' : 'Envoyer' }}
              <AppIcon name="bi-arrow-right" v-if="!paymentRequesting" class="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </template>

          <section v-else class="paypal-payment-block" aria-labelledby="paypal-payment-title">
            <h2 id="paypal-payment-title">Payer avec PayPal</h2>
            <p>Vous serez invité à confirmer le paiement sécurisé de {{ selectedPlanPriceLabel }}.</p>
            <div v-if="paypalLoading || paypalCapturing" class="paypal-loading" role="status" aria-live="polite">
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              {{ paypalCapturing ? 'Confirmation du paiement...' : 'Chargement de PayPal...' }}
            </div>
            <div v-if="!paypalCompleted" ref="paypalButtons" class="paypal-buttons"></div>
            <p v-if="paypalCompleted" class="paypal-complete-note">
              <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" aria-hidden="true" />
              Paiement confirmé. Votre abonnement est actif.
            </p>
            <small v-if="paypalEnvironment === 'sandbox'">Mode Sandbox : utilisez un compte acheteur PayPal Sandbox.</small>
          </section>

          <p v-if="paymentMessage" class="payment-message" :class="{ success: paymentSuccess, error: !paymentSuccess }" role="status" aria-live="polite">
            <AppIcon :name="paymentSuccess ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'" :class="paymentSuccess ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'" aria-hidden="true" />
            {{ paymentMessage }}
          </p>

          <p class="payment-help">
            Besoin d’aide ?
            <a :href="whatsappPaymentLink" target="_blank" rel="noopener noreferrer">
              <AppIcon name="bi-whatsapp" class="bi bi-whatsapp" aria-hidden="true" />
              Contactez-nous sur WhatsApp
            </a>
          </p>
        </div>
      </section>

      <div v-if="!showPaymentOnly" class="billing-switch mx-auto mb-4" role="group" aria-label="Choisir la période de facturation">
        <button
          type="button"
          :class="{ active: billingCycle === 'monthly' }"
          :aria-pressed="billingCycle === 'monthly'"
          @click="setBillingCycle('monthly')"
        >
          Mensuel
        </button>
        <button
          type="button"
          :class="{ active: billingCycle === 'yearly' }"
          :aria-pressed="billingCycle === 'yearly'"
          aria-label="Facturation annuelle, 2 mois offerts"
          @click="setBillingCycle('yearly')"
        >
          <span>2 mois offerts</span>
        </button>
      </div>

      <section v-if="!showPaymentOnly" class="comparison-panel" aria-label="Comparaison des plans GStocky">
        <div v-if="planUpdatingKey" class="plan-update-overlay" aria-live="polite">
          <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
          <span>Mise à jour du plan...</span>
        </div>
        <div class="mobile-pricing-list" aria-label="Choisir un plan GStocky">
          <article
            v-for="plan in plans"
            :key="`mobile-${plan.key}`"
            class="mobile-pricing-card"
            :class="{ recommended: plan.recommended, selected: isSelectedPlan(plan), disabled: Boolean(planUpdatingKey) }"
            role="button"
            tabindex="0"
            @click="!planUpdatingKey && choosePlan(plan)"
            @keydown.enter.prevent="!planUpdatingKey && choosePlan(plan)"
            @keydown.space.prevent="!planUpdatingKey && choosePlan(plan)"
          >
            <div class="mobile-plan-top">
              <div class="mobile-plan-icon">
                <AppIcon :name="plan.icon || 'bi bi-box-seam'" :class="plan.icon || 'bi bi-box-seam'" />
              </div>
              <div>
                <div class="mobile-plan-title-row">
                  <h2>{{ plan.name }}</h2>
                  <span v-if="plan.recommended">Populaire</span>
                  <span v-if="isSelectedPlan(plan)" class="selected">Choisi</span>
                </div>
                <p>{{ plan.audience }}</p>
              </div>
            </div>

            <div class="mobile-plan-price" :class="{ enterprise: plan.enterprise }">
              <template v-if="plan.enterprise">
                <strong>Sur devis</strong>
              </template>
              <template v-else>
                <span v-if="activeDiscountPercent" class="plan-price-original">{{ formatAmount(plan[billingCycle]) }} {{ pricingCurrencySymbol }} HT</span>
                <strong>{{ formatAmount(discountedPlanPrice(plan)) }}</strong>
                <span v-if="activeDiscountPercent" class="plan-discount-badge">-{{ activeDiscountPercent }}%</span>
                <span>{{ pricingCurrencySymbol }} HT / {{ billingCycle === 'monthly' ? 'mois' : 'an' }}</span>
              </template>
            </div>

            <div class="mobile-plan-limits">
              <span><AppIcon name="bi-people" class="bi bi-people" />{{ plan.users }}</span>
              <span><AppIcon name="bi-boxes" class="bi bi-boxes" />{{ plan.warehouses }}</span>
            </div>

            <ul class="mobile-plan-features">
              <li v-for="feature in plan.keyFeatures.slice(0, 4)" :key="feature">
                <AppIcon name="bi-check2" class="bi bi-check2" />
                <span>{{ feature }}</span>
              </li>
            </ul>

            <button
              type="button"
              class="mobile-plan-action"
              :class="{ primary: plan.recommended || isSelectedPlan(plan) }"
              :disabled="Boolean(planUpdatingKey)"
              @click.stop="choosePlan(plan)"
            >
              <span v-if="planUpdatingKey === plan.key" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ planUpdatingKey === plan.key ? 'Enregistrement...' : (isSelectedPlan(plan) ? 'Sélectionné' : (plan.enterprise ? 'Contacter' : 'Choisir')) }}
            </button>
          </article>
        </div>
        <div class="comparison-table-wrap">
          <table class="comparison-table">
            <colgroup>
              <col>
              <col
                v-for="plan in plans"
                :key="`col-${plan.key}`"
                :class="{ active: hoveredPlanKey === plan.key, selected: isSelectedPlan(plan) }"
              >
            </colgroup>
            <thead>
              <tr>
                <th>Fonctionnalité</th>
                <th
                  v-for="plan in plans"
                  :key="plan.key"
                  class="plan-choice-cell"
                  :class="{ recommended: plan.recommended, selected: isSelectedPlan(plan), hovered: hoveredPlanKey === plan.key }"
                  role="button"
                  tabindex="0"
                  @mouseenter="hoveredPlanKey = plan.key"
                  @mouseleave="hoveredPlanKey = null"
                  @click="!planUpdatingKey && choosePlan(plan)"
                  @keydown.enter.prevent="!planUpdatingKey && choosePlan(plan)"
                  @keydown.space.prevent="!planUpdatingKey && choosePlan(plan)"
                >
                  {{ plan.name }}
                  <span v-if="isSelectedPlan(plan)" class="selected-plan-chip">Plan choisi</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparisonRows" :key="row.label">
                <th>{{ row.label }}</th>
                <td
                  v-for="plan in plans"
                  :key="`${row.label}-${plan.key}`"
                  :class="{ recommended: plan.recommended, selected: isSelectedPlan(plan), hovered: hoveredPlanKey === plan.key }"
                  @mouseenter="hoveredPlanKey = plan.key"
                  @mouseleave="hoveredPlanKey = null"
                  @click="!planUpdatingKey && choosePlan(plan)"
                >
                  <span
                    class="comparison-value"
                    :class="{
                      included: row.values[plan.key] === true,
                      unavailable: row.values[plan.key] === false
                    }"
                  >
                    <AppIcon name="bi-check2" v-if="row.values[plan.key] === true" class="bi bi-check2" />
                    <AppIcon name="bi-x-lg" v-else-if="row.values[plan.key] === false" class="bi bi-x-lg" />
                    <template v-else>{{ row.values[plan.key] }}</template>
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>Action</th>
                <td
                  v-for="plan in plans"
                  :key="`choose-${plan.key}`"
                  :class="{ recommended: plan.recommended, selected: isSelectedPlan(plan), hovered: hoveredPlanKey === plan.key }"
                  @mouseenter="hoveredPlanKey = plan.key"
                  @mouseleave="hoveredPlanKey = null"
                  @click="!planUpdatingKey && choosePlan(plan)"
                >
                  <button
                    type="button"
                    class="choose-plan-btn"
                    :class="{ primary: plan.recommended }"
                    :disabled="Boolean(planUpdatingKey)"
                    @click.stop="choosePlan(plan)"
                  >
                    <span v-if="planUpdatingKey === plan.key" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ planUpdatingKey === plan.key ? 'Enregistrement...' : (isSelectedPlan(plan) ? 'Sélectionné' : (plan.enterprise ? 'Contacter' : 'Choisir')) }}
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

    </div>
  </section>
</template>

<script>
import { PRICING_ADDONS, PRICING_MARKET_NOTES, PRICING_PLANS, applyDiscountToPrice, canPlanAccessModule, canPlanAccessModuleItem, convertPlanPrice, formatPlanAmount, formatPlanPrice, getBrowserPricingCountryCode, getBusinessPlanKey, getPlanCurrencySymbol, getPricingCurrency, getStoredPlanIntent, normalizeDiscountPercent, savePlanIntentFromQuery } from '@/constants/pricingPlans'
import helper from '@/helper'

let paypalSdkPromise = null
let paypalSdkKey = null

export default {
  name: 'Subscribe',
  data() {
    return {
      billingCycle: 'monthly',
      showComparison: false,
      fromOnboarding: false,
      selectedPlanKey: null,
      selectedBillingCycleKey: 'monthly',
      hoveredPlanKey: null,
      selectedFile: null,
      isDragging: false,
      ribCopied: false,
      ribCopyResetTimer: null,
      paymentRequesting: false,
      planUpdatingKey: null,
      paymentMessage: '',
      paymentSuccess: false,
      paymentMethod: 'bank_transfer',
      paypalLoading: false,
      paypalCapturing: false,
      paypalCompleted: false,
      paypalEnvironment: null,
      paypalButtonsInstance: null,
      visitorCountryCode: null,
      ribNumber: '022780000760004411017674',
      plans: PRICING_PLANS,
      marketNotes: PRICING_MARKET_NOTES,
      addons: PRICING_ADDONS
    }
  },
  async mounted() {
    await this.detectVisitorCountry();
    this.applyRouteSelection();
    if (window.mixpanel) {
      window.mixpanel.track('subscribe');
    }
    this.loadCrispChat();
  },
  beforeDestroy() {
    if (this.ribCopyResetTimer) clearTimeout(this.ribCopyResetTimer);
    if (this.paypalButtonsInstance?.close) this.paypalButtonsInstance.close();
    this.removeCrispChat();
  },
  methods: {
    selectPaymentMethod(method) {
      if (!['bank_transfer', 'paypal'].includes(method)) return;
      if (method === 'paypal' && this.isMorocco) return;

      this.paymentMethod = method;
      this.paymentMessage = '';
      this.paymentSuccess = false;

      if (method === 'paypal') {
        this.$nextTick(() => this.renderPayPalButtons());
      } else if (this.paypalButtonsInstance?.close) {
        this.paypalButtonsInstance.close();
        this.paypalButtonsInstance = null;
      }
    },
    async loadPayPalSdk() {
      const response = await helper.customGet(
        `${process.env.VUE_APP_API}/billing/paypal/config`,
        { silent: true }
      );
      const config = response.data || {};
      if (!config.available) throw new Error('PayPal est réservé aux clients internationaux.');
      if (!config.client_id) throw new Error('PayPal n’est pas configuré.');
      this.paypalEnvironment = config.environment || null;

      const sdkKey = `${config.client_id}:${config.currency || 'USD'}`;
      if (window.paypal && paypalSdkKey === sdkKey) return window.paypal;
      if (paypalSdkPromise && paypalSdkKey === sdkKey) return paypalSdkPromise;

      paypalSdkKey = sdkKey;
      paypalSdkPromise = new Promise((resolve, reject) => {
        const previousScript = document.getElementById('paypal-checkout-sdk');
        if (previousScript) previousScript.remove();
        if (window.paypal) delete window.paypal;

        const script = document.createElement('script');
        script.id = 'paypal-checkout-sdk';
        script.async = true;
        script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(config.client_id)}&currency=${encodeURIComponent(config.currency || 'USD')}&intent=capture&components=buttons`;
        script.onload = () => resolve(window.paypal);
        script.onerror = () => {
          paypalSdkPromise = null;
          reject(new Error('Impossible de charger PayPal. Vérifiez votre connexion.'));
        };
        document.head.appendChild(script);
      });

      return paypalSdkPromise;
    },
    async renderPayPalButtons() {
      if (this.paymentMethod !== 'paypal' || this.paypalCompleted) return;
      this.paypalLoading = true;
      this.paymentMessage = '';

      try {
        const paypal = await this.loadPayPalSdk();
        if (!this.$refs.paypalButtons || this.paymentMethod !== 'paypal') return;

        if (this.paypalButtonsInstance?.close) await this.paypalButtonsInstance.close();
        this.$refs.paypalButtons.innerHTML = '';

        const buttons = paypal.Buttons({
          fundingSource: paypal.FUNDING.PAYPAL,
          style: {
            layout: 'vertical',
            shape: 'rect',
            label: 'paypal',
            height: 44
          },
          createOrder: async () => {
            const response = await helper.customPost(
              `${process.env.VUE_APP_API}/billing/paypal/orders`,
              {
                plan_key: this.selectedPlan.key,
                billing_cycle: this.billingCycle,
                offer_code: this.activeOfferCode
              },
              { silent: true }
            );
            return response.data.id;
          },
          onApprove: async (data) => {
            this.paypalCapturing = true;
            this.paymentMessage = '';
            try {
              const response = await helper.customPost(
                `${process.env.VUE_APP_API}/billing/paypal/orders/${encodeURIComponent(data.orderID)}/capture`,
                {
                  plan_key: this.selectedPlan.key,
                  billing_cycle: this.billingCycle,
                  offer_code: this.activeOfferCode
                },
                { silent: true }
              );

              if (!response.data?.success) throw new Error('Le paiement PayPal n’a pas pu être confirmé.');
              if (response.data.business) this.$store.commit('SET_BUSINESS', response.data.business);
              this.paypalCompleted = true;
              this.paymentSuccess = true;
              this.paymentMessage = 'Paiement PayPal confirmé. Votre abonnement est maintenant actif.';
            } catch (error) {
              this.paymentSuccess = false;
              this.paymentMessage = error?.response?.data?.message || error.message || 'Impossible de confirmer le paiement PayPal.';
              throw error;
            } finally {
              this.paypalCapturing = false;
            }
          },
          onCancel: () => {
            this.paymentSuccess = false;
            this.paymentMessage = 'Paiement PayPal annulé. Aucun montant n’a été débité.';
          },
          onError: (error) => {
            this.paypalCapturing = false;
            this.paymentSuccess = false;
            if (!this.paymentMessage) {
              this.paymentMessage = error?.message || 'Une erreur PayPal est survenue. Veuillez réessayer.';
            }
          }
        });

        if (!buttons.isEligible()) throw new Error('PayPal n’est pas disponible pour ce navigateur ou ce compte.');
        this.paypalButtonsInstance = buttons;
        await buttons.render(this.$refs.paypalButtons);
      } catch (error) {
        this.paymentSuccess = false;
        this.paymentMessage = error?.response?.data?.message || error.message || 'Impossible de charger PayPal.';
      } finally {
        this.paypalLoading = false;
      }
    },
    async detectVisitorCountry() {
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/location/country`,
          { silent: true, timeout: 4000 }
        );
        const countryCode = String(response.data?.country_code || '').toUpperCase();
        if (/^[A-Z]{2}$/.test(countryCode)) this.visitorCountryCode = countryCode;
      } catch (error) {
        // The saved business country remains the fallback when edge location headers are unavailable.
      }

      if (!this.visitorCountryCode) {
        this.visitorCountryCode = getBrowserPricingCountryCode();
      }
    },
    loadCrispChat() {
      if (window.$crisp) return;
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = '3aa4fac3-42c0-4b5d-9e93-19d7def099d1';

      const script = document.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;
      script.id = 'crisp-chat-script';
      document.getElementsByTagName('head')[0].appendChild(script);
    },
    removeCrispChat() {
      const crispScript = document.getElementById('crisp-chat-script');
      if (crispScript) crispScript.remove();
      const crispWidget = document.getElementById('crisp-chatbox');
      if (crispWidget) crispWidget.remove();
      if (window.Crisp) delete window.Crisp;
      if (window.$crisp) delete window.$crisp;
    },
    formatPrice(price) {
      return formatPlanPrice(this.priceWithVat(price), this.pricingCurrency);
    },
    formatHtPrice(price) {
      const amount = formatPlanAmount(price, this.pricingCurrency);
      return this.pricingCurrency === 'USD' ? `$${amount} HT` : `${amount} DH HT`;
    },
    formatDiscountedHtPrice(price, suffix) {
      const original = this.formatHtPrice(price);
      if (!this.activeDiscountPercent) return `${original} ${suffix}`;
      return `${this.formatHtPrice(applyDiscountToPrice(price, this.activeDiscountPercent))} ${suffix} au lieu de ${original}`;
    },
    formatAmount(price) {
      return formatPlanAmount(price, this.pricingCurrency);
    },
    priceWithVat(price) {
      return Math.round(Number(price || 0) * 1.2);
    },
    vatAmount(price) {
      return this.priceWithVat(price) - Number(price || 0);
    },
    discountedPlanPrice(plan) {
      if (!plan || plan.enterprise) return 0;
      return applyDiscountToPrice(plan[this.billingCycle], this.activeDiscountPercent);
    },
    applyRouteSelection() {
      savePlanIntentFromQuery(this.$route.query);
      const explicitBusinessPlan = this.getExplicitBusinessPlanKey();
      const requestedPlan = this.$route?.query?.plan || explicitBusinessPlan || getBusinessPlanKey(this.business);
      const selectedPlan = this.plans.find(plan => plan.key === requestedPlan);
      const requestedBilling = this.$route?.query?.interval || this.$route?.query?.billing || this.business?.metadata?.billing_cycle;

      this.selectedPlanKey = selectedPlan?.key || null;
      this.fromOnboarding = this.$route?.query?.from === 'onboarding';

      if (['monthly', 'yearly'].includes(requestedBilling)) {
        this.billingCycle = requestedBilling;
      }
      this.selectedBillingCycleKey = ['monthly', 'yearly'].includes(requestedBilling) ? requestedBilling : this.billingCycle;

      if (this.$route?.query?.payment === 'true') {
        if (selectedPlan && !selectedPlan.enterprise && (this.$route?.query?.plan || explicitBusinessPlan)) {
          this.scrollToPaymentPanel();
        } else {
          this.$router.replace({ path: '/subscribe' }).catch(() => {});
        }
      }
    },
    getExplicitBusinessPlanKey() {
      return this.business?.metadata?.plan_key || this.business?.metadata?.pricing_plan || this.business?.plan_id || null;
    },
    normalizeMetadata(metadata) {
      if (!metadata) return {};
      if (typeof metadata === 'string') {
        try {
          return JSON.parse(metadata);
        } catch (e) {
          return {};
        }
      }
      return metadata;
    },
    async setBillingCycle(cycle) {
      if (!['monthly', 'yearly'].includes(cycle)) return;
      this.billingCycle = cycle;

      if (this.showPaymentOnly) {
        this.$router.replace({
          path: '/subscribe',
          query: {
            ...this.$route.query,
            billing: cycle
          }
        }).catch(() => {});

        if (this.selectedPlan) {
          await this.persistSelectedPlan(this.selectedPlan, { syncModules: false });
        }
      }
    },
    isSelectedPlan(plan) {
      return this.selectedPlanKey === plan.key && this.selectedBillingCycleKey === this.billingCycle;
    },
    monthlyEquivalent(plan) {
      return Math.round(plan.yearly / 12);
    },
    planVatAmount(plan) {
      return this.vatAmount(plan?.[this.billingCycle]);
    },
    secondaryFeatures(plan) {
      const keyFeatureSet = new Set(plan.keyFeatures || []);
      return (plan.features || []).filter(feature => !keyFeatureSet.has(feature));
    },
    generatePurchaseLink(plan) {
      const billingProductId = 'b380cbd7-b244-4359-ad59-d120f0309a86';
      const query = [
        ['quantity', 1],
        ['discount', this.activeDiscountPercent],
        ['checkout[email]', this.user?.email || ''],
        ['checkout[custom][business_id]', this.business.id],
        ['checkout[custom][plan]', plan.key],
        ['checkout[custom][plan_id]', plan.key],
        ['checkout[custom][billing_cycle]', this.billingCycle],
        ['checkout[custom][offer_code]', this.activeOfferCode],
        ['checkout[custom][discount_percent]', this.activeDiscountPercent],
        ['checkout[custom][currency]', this.pricingCurrency],
        ['checkout[custom][total]', convertPlanPrice(this.priceWithVat(this.discountedPlanPrice(plan)), this.pricingCurrency)]
      ]
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

      return `https://gstocky.lemonsqueezy.com/checkout/buy/${billingProductId}?${query}`;
    },
    contactForPlan(plan) {
      this.trackPlanIntent(plan);
      const amount = plan.enterprise ? 'sur devis' : `${this.formatPrice(this.discountedPlanPrice(plan))} / ${this.billingCycle === 'monthly' ? 'mois' : 'an'}`;
      const message = `Bonjour, je souhaite souscrire au plan GStocky ${plan.name} (${this.selectedBillingLabel}, ${amount}).`;
      window.open(`https://wa.me/212656477702?text=${encodeURIComponent(message)}`, '_blank');
    },
    async choosePlan(plan) {
      if (this.planUpdatingKey) return;

      if (plan.enterprise) {
        this.contactForPlan(plan);
        return;
      }

      if (!this.isMorocco) {
        const saved = await this.persistSelectedPlan(plan);
        if (saved) {
          window.location.href = this.generatePurchaseLink(plan);
        }
        return;
      }

      await this.selectPlanForPayment(plan, true);
    },
    async persistSelectedPlan(plan, options = {}) {
      if (!plan || plan.enterprise) return false;

      const shouldSyncModules = options.syncModules !== false;
      this.planUpdatingKey = plan.key;
      this.paymentMessage = '';
      this.paymentSuccess = false;

      const metadata = {
        ...this.normalizeMetadata(this.business?.metadata),
        plan_key: plan.key,
        pricing_plan: plan.key,
        plan_name: plan.name,
        billing_cycle: this.billingCycle,
        offer_code: this.activeOfferCode,
        discount_percent: this.activeDiscountPercent,
        subscription_discount_percent: this.activeDiscountPercent,
        subscription_currency: this.pricingCurrency,
        original_plan_price: convertPlanPrice(plan[this.billingCycle], this.pricingCurrency),
        discounted_plan_price: convertPlanPrice(this.discountedPlanPrice(plan), this.pricingCurrency),
        subscription_status: 'pending_payment'
      };

      try {
        const response = await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          {
            plan_id: plan.key,
            metadata
          },
          { showToast: false }
        );

        const updatedBusiness = {
          ...(this.business || {}),
          ...(response?.data || {}),
          plan_id: plan.key,
          metadata: response?.data?.metadata ? this.normalizeMetadata(response.data.metadata) : metadata
        };

        this.$store.commit('SET_BUSINESS', updatedBusiness);
        this.selectedPlanKey = plan.key;
        this.selectedBillingCycleKey = this.billingCycle;
        if (shouldSyncModules) {
          await this.syncModulesWithPlan(plan.key);
        }
        return true;
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Impossible de mettre à jour le plan. Veuillez réessayer.';
        this.paymentMessage = message;
        this.paymentSuccess = false;
        window.vueOpener?.open?.({
          message,
          type: 'error',
          position: 'bottom'
        });
        return false;
      } finally {
        this.planUpdatingKey = null;
      }
    },
    async syncModulesWithPlan(planKey) {
      let modules = Array.isArray(this.modules) ? this.modules : [];

      if (!modules.length) {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/modules`);
        modules = Array.isArray(response?.data) ? response.data : [];
      }

      if (!modules.length) return;

      const syncedModules = modules.map(module => {
        const isSettings = module.module_key === 'settings';
        const isAvailable = isSettings || canPlanAccessModule(planKey, module.module_key);

        return {
          ...module,
          enabled: isSettings ? true : isAvailable,
          expanded: ['business', 'pro'].includes(planKey) ? false : (planKey === 'starter' ? isAvailable : module.expanded),
          items: module.items
            ? module.items.map(item => {
                const isOptInReceipt = module.module_key === 'sales' && item.title === 'processes';
                return {
                  ...item,
                  enabled: isAvailable &&
                    (isSettings || canPlanAccessModuleItem(planKey, module.module_key, item.permission, item.title)) &&
                    (!isOptInReceipt || item.enabled === true)
                };
              })
            : []
        };
      });

      this.$store.commit('SET_MODULES', syncedModules);
      await helper.customPost(`${process.env.VUE_APP_API}/modules`, syncedModules, { showToast: false });
    },
    async selectPlanForPayment(plan, shouldScroll = true) {
      if (!plan || plan.enterprise) {
        this.$router.push({ path: '/subscribe' });
        return;
      }

      const saved = await this.persistSelectedPlan(plan);
      if (!saved) return;

      this.trackPlanIntent(plan);

      this.$router.replace({
        path: '/subscribe',
        query: {
          ...this.$route.query,
          plan: plan.key,
          billing: this.billingCycle,
          payment: 'true'
        }
      }).catch(() => {});

      if (shouldScroll) this.scrollToPaymentPanel();
    },
    scrollToPaymentPanel() {
      this.$nextTick(() => {
        this.$refs.paymentPanel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    async copyRIB() {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.ribNumber);
        } else {
          const input = document.createElement('textarea');
          input.value = this.ribNumber;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }

        this.ribCopied = true;
        if (this.ribCopyResetTimer) clearTimeout(this.ribCopyResetTimer);
        this.ribCopyResetTimer = setTimeout(() => {
          this.ribCopied = false;
        }, 2200);
      } catch (error) {
        window.vueOpener?.open?.({
          message: 'Impossible de copier le RIB. Sélectionnez-le manuellement.',
          type: 'error',
          position: 'bottom'
        });
      }
    },
    openReceiptPicker() {
      this.$refs.receiptInput?.click();
    },
    clearSelectedFile() {
      this.selectedFile = null;
      if (this.$refs.receiptInput) this.$refs.receiptInput.value = '';
    },
    setSelectedFile(file) {
      this.paymentMessage = '';
      this.paymentSuccess = false;

      if (!file) return;

      const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      const hasAcceptedType = acceptedTypes.includes(file.type) || /\.(jpe?g|png|webp|pdf)$/i.test(file.name || '');
      if (!hasAcceptedType) {
        this.clearSelectedFile();
        this.paymentMessage = 'Format non pris en charge. Ajoutez une image JPG, PNG, WEBP ou un PDF.';
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.clearSelectedFile();
        this.paymentMessage = 'Ce fichier dépasse 10 Mo. Choisissez un justificatif plus léger.';
        return;
      }

      this.selectedFile = file;
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      if (files.length) this.setSelectedFile(files[0]);
    },
    handleDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length) this.setSelectedFile(files[0]);
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
    },
    async uploadReceipt() {
      if (!this.selectedPlan) {
        this.$router.push({ path: '/subscribe' });
        return;
      }
      if (!this.selectedFile) return;

      this.paymentRequesting = true;
      this.paymentMessage = '';

      try {
        let folderId = null;
        const foldersResp = await helper.customGet(`${process.env.VUE_APP_API}/folders`);
        const folders = Array.isArray(foldersResp.data) ? foldersResp.data : [];
        const billingFolder = folders.find(folder => folder.name?.toLowerCase() === 'billing');

        if (billingFolder) {
          folderId = billingFolder.id;
        } else {
          try {
            const createFolderResp = await helper.customPost(`${process.env.VUE_APP_API}/folders`, {
              name: 'billing',
              is_private: false
            });
            folderId = createFolderResp.data.id;
          } catch (e) {
            folderId = null;
          }
        }

        const uploadPath = `businesses/${this.business.id}/attachments/${helper.generateRandString(20)}`;
        await helper.customUploadFile(this.selectedFile, uploadPath, {
          entity_type: 'business',
          entity_id: this.business.id,
          folder_id: folderId,
          notes: `Subscription receipt - ${this.selectedPlan.name} (${this.selectedBillingLabel}) - ${this.user?.email || ''}`
        });

        this.paymentSuccess = true;
        this.paymentMessage = 'Reçu envoyé avec succès. Votre abonnement sera activé après vérification.';
        this.clearSelectedFile();
      } catch (error) {
        this.paymentSuccess = false;
        this.paymentMessage = error.message || 'Erreur lors de l’envoi du reçu. Veuillez réessayer.';
      } finally {
        this.paymentRequesting = false;
      }
    },
    trackPlanIntent(plan) {
      if (window.fbq && !plan.enterprise) {
        window.fbq('track', 'InitiateCheckout', {
          currency: this.pricingCurrency,
          value: convertPlanPrice(this.discountedPlanPrice(plan), this.pricingCurrency)
        });
      }
      if (window.mixpanel) {
        window.mixpanel.track('pricing_plan_cta_clicked', {
          plan: plan.key,
          billing_cycle: this.billingCycle,
          offer_code: this.activeOfferCode,
          discount_percent: this.activeDiscountPercent,
          price: plan.enterprise ? null : convertPlanPrice(this.discountedPlanPrice(plan), this.pricingCurrency),
          currency: this.pricingCurrency
        });
      }
    }
  },
  watch: {
    isMorocco(isMoroccan) {
      if (isMoroccan && this.paymentMethod === 'paypal') {
        this.selectPaymentMethod('bank_transfer');
      }
    }
  },
  computed: {
    business() { return this.$store.getters.BUSINESS },
    user() { return this.$store.getters.USER },
    modules() { return this.$store.state.modules },
    selectedPlan() {
      return this.plans.find(plan => plan.key === this.selectedPlanKey);
    },
    showPaymentOnly() {
      return this.$route?.query?.payment === 'true' && this.selectedPlan && !this.selectedPlan.enterprise;
    },
    nonEnterprisePlans() {
      return this.plans.filter(plan => !plan.enterprise);
    },
    activeDiscountPercent() {
      const storedIntent = getStoredPlanIntent();
      return normalizeDiscountPercent(
        storedIntent?.discount ??
        this.business?.metadata?.discount_percent ??
        this.business?.metadata?.subscription_discount_percent
      );
    },
    activeOfferCode() {
      const storedIntent = getStoredPlanIntent();
      return storedIntent?.offer || storedIntent?.offer_code || this.business?.metadata?.offer_code || null;
    },
    selectedPlanPriceLabel() {
      if (!this.selectedPlan || this.selectedPlan.enterprise) return 'Sur devis';
      const amount = this.formatAmount(this.discountedPlanPrice(this.selectedPlan));
      return this.pricingCurrency === 'USD' ? `$${amount}` : `${amount} DH`;
    },
    whatsappPaymentLink() {
      const message = `Bonjour, j’ai besoin d’aide pour finaliser mon abonnement GStocky ${this.selectedPlan?.name || ''}.`;
      return `https://wa.me/212656477702?text=${encodeURIComponent(message)}`;
    },
    heroSubtitle() {
      if (this.showPaymentOnly && this.selectedPlan) {
        return `Envoyez votre reçu de virement pour le plan ${this.selectedPlan.name}.`;
      }
      if (this.fromOnboarding && this.selectedPlan) {
        return `Votre plan ${this.selectedPlan.name} est prêt. Choisissez mensuel ou annuel pour finaliser.`;
      }
      return 'Choisissez le plan adapté à votre activité.';
    },
    isMorocco() {
      if (this.visitorCountryCode) return this.visitorCountryCode === 'MA';
      const country = this.business?.country?.toLowerCase() || '';
      const currency = this.business?.currency?.toLowerCase() || '';
      return country === 'ma' || country === 'morocco' || currency === 'mad' || currency === 'dh';
    },
    pricingCurrency() {
      return getPricingCurrency(this.isMorocco ? 'MA' : 'US');
    },
    pricingCurrencySymbol() {
      return getPlanCurrencySymbol(this.pricingCurrency);
    },
    selectedBillingLabel() {
      return this.billingCycle === 'monthly' ? 'mensuel' : 'annuel';
    },
    comparisonRows() {
      const priceRow = this.billingCycle === 'monthly'
        ? {
            label: 'Prix mensuel choisi',
            values: {
              starter: this.formatDiscountedHtPrice(149, '/ mois'),
              business: this.formatDiscountedHtPrice(249, '/ mois'),
              pro: this.formatDiscountedHtPrice(499, '/ mois'),
              enterprise: 'Sur devis'
            }
          }
        : {
            label: 'Prix annuel choisi (2 mois offerts)',
            values: {
              starter: this.formatDiscountedHtPrice(1490, '/ an'),
              business: this.formatDiscountedHtPrice(2490, '/ an'),
              pro: this.formatDiscountedHtPrice(4990, '/ an'),
              enterprise: 'Sur devis'
            }
          };

      return [
        priceRow,
        { label: 'Utilisateurs', values: { starter: '1', business: '3', pro: '10', enterprise: 'Illimités' } },
        { label: 'Gestion des dépôts', values: { starter: 'Stock principal', business: 'Magasin + réserve + entrepôt', pro: 'Multi-site illimité', enterprise: 'Personnalisé' } },
        { label: 'Sociétés', values: { starter: '1', business: '1', pro: '1', enterprise: 'Multi-société' } },
        { label: 'Stockage', values: { starter: '5 Go', business: '20 Go', pro: '50 Go', enterprise: 'Personnalisé' } },
        { label: 'Aide import Excel', values: { starter: 'Self-service', business: true, pro: true, enterprise: 'Migration accompagnée' } },
        { label: 'Clients et produits', values: { starter: true, business: true, pro: true, enterprise: true } },
        { label: 'Devis, factures et BL', values: { starter: true, business: true, pro: true, enterprise: true } },
        { label: 'Paiements', values: { starter: true, business: true, pro: true, enterprise: true } },
        { label: 'Stock de base', values: { starter: true, business: true, pro: true, enterprise: true } },
        { label: 'Ventes et achats', values: { starter: false, business: true, pro: true, enterprise: true } },
        { label: 'Fournisseurs', values: { starter: false, business: true, pro: true, enterprise: true } },
        { label: 'Trésorerie', values: { starter: false, business: true, pro: true, enterprise: true } },
        { label: 'Pièces jointes', values: { starter: false, business: true, pro: true, enterprise: true } },
        { label: 'Documents clients/fournisseurs', values: { starter: false, business: true, pro: true, enterprise: true } },
        { label: 'POS', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Multi-dépôt avancé', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Rapports avancés', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Projets', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Abonnements', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Réservations', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'GStocky AI', values: { starter: false, business: false, pro: '100 imports / mois', enterprise: 'Personnalisé' } },
        { label: 'Caisses POS incluses', values: { starter: false, business: false, pro: '2', enterprise: 'Personnalisé' } },
        { label: 'Support prioritaire', values: { starter: false, business: false, pro: true, enterprise: true } },
        { label: 'Marketing', values: { starter: false, business: false, pro: false, enterprise: true } },
        { label: 'Multi-société', values: { starter: false, business: false, pro: false, enterprise: true } },
        { label: 'Templates personnalisés', values: { starter: false, business: false, pro: false, enterprise: true } },
        { label: 'Onboarding et formation', values: { starter: false, business: false, pro: false, enterprise: true } },
        { label: 'Intégrations spécifiques', values: { starter: false, business: false, pro: false, enterprise: true } }
      ];
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

.subscribe-page {
  background: var(--app-background, #f5f6fa);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;
}

.header-band {
  display: none;
}

.pricing-hero {
  max-width: 860px;
}

.pricing-hero h1 {
  font-size: clamp(2.1rem, 5vw, 3.5rem);
  letter-spacing: 0;
  line-height: 1.08;
}

.fw-800 { font-weight: 800; }

.eyebrow {
  color: #2563eb;
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.billing-switch {
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 300px;
  padding: 3px;
}

.billing-switch button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #475569;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 5px;
  justify-content: center;
  min-height: 32px;
  padding: 0 8px;
}

.billing-switch button.active {
  background: #004aad;
  color: #fff;
}

.billing-switch span {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  font-size: 0.58rem;
  padding: 2px 5px;
}

.market-notes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.market-notes span,
.pricing-addons span {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 999px;
  color: #334155;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 7px;
  min-height: 34px;
  padding: 0 12px;
}

.market-notes i {
  color: #16a34a;
}

.plan-card {
  background: #fff;
  border: 1px solid #dfe7f0;
  border-radius: 8px;
  box-shadow: 0 16px 42px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  padding: 24px;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.plan-card:hover {
  border-color: #b9c7d9;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.11);
}

.plan-card.recommended {
  border-color: #2563eb;
  box-shadow: 0 24px 60px rgba(37, 99, 235, 0.18);
  transform: translateY(-8px);
}

.plan-card.selected {
  border-color: #16a34a;
  box-shadow: 0 24px 60px rgba(22, 163, 74, 0.18);
}

.plan-card.recommended.selected {
  border-color: #2563eb;
  box-shadow: 0 24px 60px rgba(37, 99, 235, 0.2), 0 0 0 3px rgba(22, 163, 74, 0.16);
}

.popular-badge {
  background: #1d4ed8;
  border-radius: 6px;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 6px 10px;
  position: absolute;
  left: -1px;
  text-transform: uppercase;
  top: -14px;
  transform: rotate(0deg);
  transform-origin: center;
}

.selected-badge {
  background: #16a34a;
  border-radius: 6px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 6px 10px;
  position: absolute;
  right: 12px;
  text-transform: uppercase;
  top: -14px;
}

.plan-heading {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 18px;
  min-height: 48px;
}

.audience-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.15;
  max-width: 150px;
  padding: 6px 9px;
  text-align: right;
}

.recommended .audience-pill {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.plan-icon {
  align-items: center;
  background: #eef5ff;
  border-radius: 8px;
  color: #2563eb;
  display: flex;
  font-size: 1.45rem;
  flex: 0 0 auto;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.plan-top h2 {
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 800;
  margin-bottom: 10px;
}

.plan-top p {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.55;
  margin-bottom: 0;
  min-height: 68px;
}

.price-block {
  border-bottom: 1px solid #e2e8f0;
  border-top: 1px solid #e2e8f0;
  margin: 22px 0 16px;
  padding: 18px 0;
}

.price-line {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.price-line strong {
  color: #0f172a;
  display: inline-block;
  font-size: 2.35rem;
  font-weight: 800;
  line-height: 1;
}

.price-block small {
  color: #64748b;
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  margin-top: 7px;
}

.price-currency {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
}

.price-tax {
  background: #eef2f7;
  border-radius: 5px;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 3px 5px;
}

.price-period {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 800;
}

.price-block em {
  background: #dcfce7;
  border-radius: 6px;
  color: #166534;
  display: inline-block;
  font-size: 0.72rem;
  font-style: normal;
  font-weight: 800;
  margin-top: 10px;
  padding: 5px 8px;
}

.limits {
  display: grid;
  gap: 8px;
  margin-bottom: 18px;
}

.limits span {
  align-items: center;
  color: #334155;
  display: flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 8px;
  line-height: 1.25;
}

.limits i,
.feature-list i {
  color: #2563eb;
}

.feature-list {
  display: grid;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.feature-group-title {
  color: #0f172a;
  font-size: 0.73rem;
  font-weight: 850;
  letter-spacing: 0;
  margin: 0 0 9px;
  text-transform: uppercase;
}

.feature-group-title-spaced {
  border-top: 1px solid #eef2f7;
  margin-top: 14px;
  padding-top: 13px;
}

.included-features li {
  color: #475569;
  font-size: 0.82rem;
}

.usage-features {
  margin-bottom: 28px;
}

.usage-features li {
  color: #64748b;
  font-size: 0.8rem;
}

.usage-features i {
  color: #94a3b8;
}

.feature-list li {
  align-items: flex-start;
  color: #334155;
  display: flex;
  font-size: 0.88rem;
  gap: 9px;
  line-height: 1.35;
}

.feature-list i {
  flex: 0 0 auto;
  margin-top: 1px;
}

.key-features .bi-stars {
  color: #1d4ed8;
}

.plan-cta {
  align-items: center;
  background: #0f172a;
  border: 0;
  border-radius: 8px;
  color: #fff;
  display: flex;
  font-weight: 800;
  justify-content: center;
  margin-top: auto;
  min-height: 46px;
  text-decoration: none;
  width: 100%;
}

.plan-cta:hover {
  color: #fff;
  transform: translateY(-1px);
}

.plan-cta.primary {
  background: #2563eb;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.24);
}

.plan-cta.secondary {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #0f172a;
}

.compare-action {
  margin-top: 34px;
}

.compare-toggle {
  align-items: center;
  background: #fff;
  border: 1px solid #bfd0e6;
  border-radius: 8px;
  color: #0f172a;
  display: inline-flex;
  font-weight: 800;
  gap: 9px;
  min-height: 46px;
  padding: 0 18px;
}

.compare-toggle:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.comparison-panel {
  background: #fff;
  border: 1px solid #dfe7f0;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
  margin-top: 22px;
  padding: 0;
  position: relative;
}

.mobile-pricing-list {
  display: none;
}

.mobile-pricing-card {
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 10px;
  color: #0f172a;
  cursor: pointer;
  padding: 14px;
}

.mobile-pricing-card.recommended {
  border-color: #2563eb;
}

.mobile-pricing-card.selected {
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
}

.mobile-pricing-card.disabled {
  cursor: wait;
}

.mobile-pricing-card:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.36);
  outline-offset: 3px;
}

.mobile-plan-top {
  align-items: flex-start;
  display: flex;
  gap: 10px;
}

.mobile-plan-icon {
  align-items: center;
  background: #eff6ff;
  border-radius: 8px;
  color: #2563eb;
  display: inline-flex;
  flex: 0 0 40px;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.mobile-plan-title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mobile-plan-title-row h2 {
  font-size: 1.05rem;
  font-weight: 900;
  margin: 0;
}

.mobile-plan-title-row span {
  background: #eff6ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 0.62rem;
  font-weight: 900;
  padding: 3px 7px;
  text-transform: uppercase;
}

.mobile-plan-title-row span.selected {
  background: #dcfce7;
  color: #166534;
}

.mobile-plan-top p {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 750;
  margin: 3px 0 0;
}

.mobile-plan-price {
  align-items: baseline;
  border-bottom: 1px solid #e2e8f0;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 12px 0;
  padding: 12px 0;
}

.mobile-plan-price.enterprise {
  align-items: center;
}

.mobile-plan-price strong {
  font-size: 1.85rem;
  font-weight: 900;
  line-height: 1;
}

.mobile-plan-price span {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 850;
}

.mobile-plan-price .plan-price-original {
  color: #94a3b8;
  text-decoration: line-through;
  width: 100%;
}

.mobile-plan-price .plan-discount-badge {
  background: #dcfce7;
  border-radius: 999px;
  color: #166534;
  padding: 3px 8px;
}

.mobile-plan-limits {
  display: grid;
  gap: 7px;
  margin-bottom: 12px;
}

.mobile-plan-limits span {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  display: flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 8px;
  line-height: 1.25;
  padding: 8px 10px;
}

.mobile-plan-limits i {
  color: #2563eb;
  flex: 0 0 auto;
}

.mobile-plan-features {
  display: grid;
  gap: 7px;
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
}

.mobile-plan-features li {
  align-items: flex-start;
  color: #334155;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 7px;
  line-height: 1.3;
}

.mobile-plan-features i {
  color: #16a34a;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mobile-plan-action {
  align-items: center;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  font-weight: 900;
  justify-content: center;
  min-height: 42px;
  width: 100%;
}

.mobile-plan-action.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.mobile-plan-action:disabled {
  cursor: wait;
  opacity: 0.75;
}

.plan-update-overlay {
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(2px);
  border-radius: 8px;
  bottom: 0;
  color: #0f172a;
  display: flex;
  font-size: 0.95rem;
  font-weight: 800;
  gap: 10px;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 8;
}

.comparison-head {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.comparison-head h2 {
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0;
}

.comparison-head p {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0;
  max-width: 470px;
}

.comparison-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow-x: auto;
}

.comparison-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 820px;
  width: 100%;
}

.comparison-table th,
.comparison-table td {
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.86rem;
  padding: 13px 14px;
  text-align: center;
  vertical-align: middle;
}

.comparison-table thead th {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 800;
  position: sticky;
  top: 0;
  z-index: 1;
}

.comparison-table thead th:not(:first-child) {
  font-weight: 500;
  position: sticky;
}

.comparison-table th:first-child {
  color: #0f172a;
  font-weight: 800;
  left: 0;
  min-width: 210px;
  position: sticky;
  text-align: left;
  z-index: 2;
}

.comparison-table tbody th:first-child {
  background: #fff;
}

.comparison-table thead th:first-child {
  background: #f8fafc;
  z-index: 3;
}

.comparison-table thead th.selected {
  background: #eff6ff;
  color: #1d4ed8;
  overflow: hidden;
}

.comparison-table tbody td.selected,
.comparison-table tfoot td.selected {
  background: #eff6ff;
}

.comparison-table thead th.hovered,
.comparison-table tbody td.hovered,
.comparison-table tfoot td.hovered {
  background: #f8fbff;
  border-left: 2px solid #2563eb;
  border-right: 2px solid #2563eb;
}

.comparison-table thead th.hovered {
  border-top: 2px solid #2563eb;
}

.comparison-table tfoot td.hovered {
  border-bottom: 2px solid #2563eb;
}

.comparison-table thead span {
  background: #1d4ed8;
  border-radius: 999px;
  color: #fff;
  display: block;
  font-size: 0.62rem;
  font-weight: 800;
  margin: 5px auto 0;
  padding: 3px 7px;
  width: fit-content;
}

.comparison-table thead .selected-plan-chip {
  background: #dcfce7;
  border-radius: 4px;
  color: #166534;
  font-size: 0.6rem;
  margin: 0;
  padding: 0;
  position: absolute;
  right: -46px;
  text-align: center;
  top: 18px;
  transform: rotate(45deg);
  transform-origin: center;
  white-space: nowrap;
  width: 150px;
}

.comparison-table tr:last-child th,
.comparison-table tr:last-child td {
  border-bottom: 0;
}

.comparison-table .plan-choice-cell {
  cursor: pointer;
}

.comparison-table tbody td,
.comparison-table tfoot td {
  cursor: pointer;
}

.comparison-table .plan-choice-cell:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(37, 99, 235, 0.28);
}

.comparison-table .plan-choice-cell:hover {
  background: #f8fafc;
}

.choose-plan-btn {
  align-items: center;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 800;
  justify-content: center;
  min-height: 38px;
  padding: 0 14px;
  width: 100%;
}

.choose-plan-btn.primary {
  background: transparent;
  border-color: #2563eb;
  color: #2563eb;
}

.choose-plan-btn:hover {
  transform: translateY(-1px);
}

.choose-plan-btn:disabled {
  cursor: wait;
  opacity: 0.75;
}

.comparison-value {
  align-items: center;
  display: inline-flex;
  font-weight: 500;
  justify-content: center;
  min-height: 24px;
}

.comparison-value.included {
  background: #dcfce7;
  border-radius: 50%;
  color: #166534;
  height: 24px;
  width: 24px;
}

.comparison-value.unavailable {
  color: #cbd5e1;
}

.pricing-addons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 24px;
}

.pricing-addons span {
  border-radius: 8px;
}

.checkout-shell {
  margin: 0 auto;
  max-width: 720px;
  width: 100%;
}

.checkout-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 22px;
}

.checkout-back-link,
.secure-checkout-label {
  align-items: center;
  color: #475569;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 8px;
}

.checkout-back-link {
  display: flex;
  margin-bottom: 22px;
  text-decoration: none;
  width: fit-content;
}

.checkout-back-link:hover {
  color: #2563eb;
}

.secure-checkout-label {
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 999px;
  padding: 7px 11px;
}

.secure-checkout-label i {
  color: #16a34a;
}

.checkout-layout {
  align-items: start;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(260px, 0.72fr) minmax(0, 1.55fr);
}

.order-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  color: #0f172a;
  overflow: hidden;
  padding: 22px;
  position: sticky;
  top: 24px;
}

.order-card::before {
  display: none;
}

.order-card-heading {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: auto 1fr auto;
  position: relative;
}

.selected-plan-icon {
  align-items: center;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  color: #2563eb;
  display: inline-flex;
  font-size: 1.25rem;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.order-card-heading span {
  color: #64748b;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.order-card-heading h2 {
  color: #0f172a;
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0;
}

.change-plan-link {
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 800;
  text-decoration: none;
}

.change-plan-link:hover {
  color: #1d4ed8;
}

.checkout-billing-switch {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: grid;
  gap: 4px;
  grid-template-columns: 1fr 1fr;
  margin: 22px 0 18px;
  padding: 4px;
}

.checkout-billing-switch button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  font-size: 0.83rem;
  font-weight: 800;
  justify-content: center;
  min-height: 48px;
  padding: 4px 8px;
  transition: background 0.2s ease, color 0.2s ease;
}

.checkout-billing-switch button.active {
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  color: #0f172a;
}

.checkout-billing-switch button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.checkout-billing-switch small {
  color: #15803d;
  font-size: 0.62rem;
  font-weight: 800;
}

.checkout-billing-switch .active small {
  color: #15803d;
}

.order-total {
  border-top: 1px solid #e2e8f0;
  display: grid;
  gap: 4px;
  padding-top: 18px;
}

.order-total span {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

.order-total strong {
  color: #0f172a;
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.order-total small {
  color: #15803d;
  font-size: 0.72rem;
  font-weight: 800;
}

.payment-workflow {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 28px;
}

.checkout-summary {
  align-items: end;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 28px;
  justify-content: space-between;
  padding-bottom: 22px;
}

.checkout-plan,
.checkout-total {
  display: grid;
  gap: 5px;
}

.checkout-plan > span,
.checkout-total > span {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.checkout-plan > div {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkout-plan strong {
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 800;
}

.checkout-interval {
  background: #eef2ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 3px 8px;
  text-transform: capitalize;
}

.checkout-plan a {
  color: #000;
  font-size: 0.65rem;
  font-weight: 800;
  text-decoration: none;
}

.checkout-total {
  text-align: right;
}

.checkout-total strong {
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  white-space: nowrap;
}

.payment-method-section {
  margin-top: 22px;
}

.payment-method-section > h2,
.paypal-payment-block h2 {
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
  margin: 0 0 10px;
}

.payment-method-options {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.payment-method-option {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe5f1;
  border-radius: 12px;
  color: #0f172a;
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr auto;
  min-height: 72px;
  padding: 12px;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.payment-method-option:hover {
  border-color: #93c5fd;
}

.payment-method-option.active {
  background: #eff6ff;
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.payment-method-option > span:not(.payment-method-icon) {
  display: grid;
  gap: 2px;
}

.payment-method-option strong {
  font-size: 0.78rem;
  font-weight: 800;
}

.payment-method-option small {
  color: #64748b;
  font-size: 0.66rem;
  line-height: 1.35;
}

.payment-method-icon {
  align-items: center;
  background: #f1f5f9;
  border-radius: 9px;
  color: #334155;
  display: inline-flex;
  font-size: 1rem;
  font-weight: 900;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.payment-method-icon.paypal-icon {
  background: #003087;
  color: #fff;
  font-family: Arial, sans-serif;
  font-style: italic;
}

.payment-method-check {
  color: #2563eb;
}

.paypal-payment-block {
  border-top: 1px solid #e2e8f0;
  margin-top: 24px;
  padding-top: 22px;
}

.paypal-payment-block > p {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.5;
  margin: 0 0 16px;
}

.paypal-payment-block > small {
  color: #94a3b8;
  display: block;
  font-size: 0.66rem;
  margin-top: 10px;
  text-align: center;
}

.paypal-buttons {
  min-height: 44px;
  position: relative;
  z-index: 0;
}

.paypal-loading,
.paypal-complete-note {
  align-items: center;
  border-radius: 10px;
  display: flex;
  font-size: 0.76rem;
  font-weight: 700;
  gap: 9px;
  justify-content: center;
  min-height: 48px;
  padding: 10px;
}

.paypal-loading {
  background: #f8fafc;
  color: #475569;
}

.paypal-complete-note {
  background: #ecfdf5;
  color: #047857 !important;
  margin-bottom: 0 !important;
}

.payment-block {
  margin-top: 22px;
}

.payment-block h2 {
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
  margin: 0 0 9px;
}

.payment-intro h1 {
  color: #0f172a;
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.12;
  margin: 2px 0 10px;
}

.payment-intro p {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;
  max-width: 540px;
}

.payment-step-card {
  border: 0;
  border-top: 1px solid #e2e8f0;
  border-radius: 0;
  margin-top: 24px;
  padding: 22px 0 0;
}

.payment-step-card + .payment-step-card {
  margin-top: 26px;
}

.step-heading {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}

.step-number {
  align-items: center;
  background: #eff6ff;
  border-radius: 9px;
  color: #2563eb;
  display: inline-flex;
  flex: 0 0 34px;
  font-size: 0.8rem;
  font-weight: 800;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.step-heading h2 {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
  margin: 0 0 4px;
}

.step-heading p {
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.45;
  margin: 0;
}

.step-heading p strong {
  color: #0f172a;
}

.amount-due {
  align-items: center;
  background: #eff6ff;
  border-radius: 10px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 12px 14px;
}

.amount-due span {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
}

.amount-due strong {
  color: #1d4ed8;
  font-size: 1rem;
  font-weight: 800;
  text-align: right;
}

.rib-box {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dbe5f1;
  border-radius: 11px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 13px 14px;
}

.rib-box div {
  min-width: 0;
}

.rib-box small {
  color: #64748b;
  display: block;
  font-size: 0.65rem;
  font-weight: 800;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.rib-box span {
  color: #0f172a;
  display: block;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: 0.035em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rib-box button {
  align-items: center;
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1d4ed8;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 800;
  gap: 6px;
  min-height: 36px;
  padding: 0 10px;
}

.rib-box button:hover {
  background: #eff6ff;
}

.transfer-note {
  align-items: flex-start;
  color: #64748b;
  display: flex;
  font-size: 0.72rem;
  gap: 8px;
  line-height: 1.45;
  margin-top: 10px;
}

.transfer-note i {
  color: #2563eb;
  flex: 0 0 auto;
}

.receipt-input:focus + .receipt-dropzone {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.receipt-dropzone {
  align-items: center;
  background: #f8fafc;
  border: 1.5px dashed #94a3b8;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  min-height: 132px;
  padding: 18px;
  position: relative;
  text-align: center;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.receipt-dropzone:hover,
.receipt-dropzone.active {
  background: #eff6ff;
  border-color: #2563eb;
  box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.12);
}

.receipt-dropzone:focus-visible {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.receipt-dropzone.selected {
  align-items: center;
  background: #f0fdf4;
  border-color: #86efac;
  border-style: solid;
  flex-direction: row;
  justify-content: flex-start;
  min-height: 94px;
  text-align: left;
}

.upload-icon {
  align-items: center;
  background: #e0e7ff;
  border-radius: 50%;
  color: #2563eb !important;
  display: inline-flex;
  font-size: 1.35rem !important;
  height: 48px;
  justify-content: center;
  margin-bottom: 4px;
  width: 48px;
}

.upload-icon.file-ready {
  background: #dcfce7;
  color: #16a34a !important;
  flex: 0 0 48px;
  margin: 0 6px 0 0;
}

.receipt-dropzone strong {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 800;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.receipt-dropzone > span:not(.upload-icon),
.selected-file-copy span {
  color: #64748b;
  font-size: 0.76rem;
}

.receipt-dropzone u {
  color: #2563eb;
  font-weight: 800;
  text-underline-offset: 2px;
}

.receipt-dropzone > small {
  color: #94a3b8;
  font-size: 0.66rem;
  margin-top: 5px;
}

.selected-file-copy {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.selected-file-copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-file-btn {
  align-items: center;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  display: inline-flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  margin-left: auto;
  padding: 0;
  width: 36px;
}

.payment-message {
  align-items: flex-start;
  border-radius: 10px;
  display: flex;
  font-size: 0.8rem;
  font-weight: 750;
  gap: 8px;
  line-height: 1.45;
  margin: 14px 0 0;
  padding: 11px 13px;
}

.payment-message.success {
  background: #dcfce7;
  color: #166534;
}

.payment-message.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.verification-note {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.45;
  margin-top: 20px;
}

.payment-panel-footer {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  border-top: 1px solid #e2e8f0;
  margin-top: 26px;
  padding-top: 22px;
}

.activation-note {
  align-items: flex-start;
  color: #64748b;
  display: flex;
  font-size: 0.7rem;
  gap: 8px;
  line-height: 1.4;
  max-width: none;
}

.activation-note i {
  color: #2563eb;
  flex: 0 0 auto;
}

.submit-proof-btn {
  align-items: center;
  background: #2563eb;
  border: 0;
  border-radius: 10px;
  box-shadow: none;
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 9px;
  justify-content: center;
  margin-top: 12px;
  min-height: 44px;
  padding: 0 18px;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.submit-proof-btn:not(:disabled):hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.submit-proof-btn:disabled {
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.48;
}

.payment-help {
  color: #64748b;
  font-size: 0.72rem;
  margin: 14px 0 0;
  text-align: center;
}

.payment-help a {
  color: #15803d;
  font-weight: 800;
  margin-left: 3px;
  text-decoration: none;
}

.payment-help a:hover {
  text-decoration: underline;
}

@media (max-width: 1199px) {
  .plan-card.recommended {
    transform: none;
  }

  .checkout-layout {
    gap: 20px;
    grid-template-columns: minmax(270px, 0.72fr) minmax(0, 1.45fr);
  }

  .order-card,
  .payment-workflow {
    padding: 22px;
  }
}

@media (max-width: 991px) {
  .checkout-shell {
    max-width: 720px;
  }

  .checkout-layout {
    grid-template-columns: 1fr;
  }

  .order-card {
    position: relative;
    top: auto;
  }

  .order-features {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .subscribe-page .container {
    padding-left: 14px;
    padding-right: 14px;
  }

  .pricing-hero {
    margin-bottom: 24px !important;
    text-align: left !important;
  }

  .pricing-hero h1 {
    font-size: 2rem;
  }

  .billing-switch {
    max-width: 300px;
  }

  .billing-switch button {
    flex-direction: column;
    gap: 3px;
    min-height: 38px;
  }

  .market-notes,
  .pricing-addons {
    justify-content: flex-start;
  }

  .market-notes span,
  .pricing-addons span {
    border-radius: 8px;
    justify-content: center;
    width: 100%;
  }

  .plan-top p {
    min-height: 0;
  }

  .audience-pill {
    max-width: none;
  }

  .comparison-panel {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
  }

  .mobile-pricing-list {
    display: grid;
    gap: 12px;
  }

  .comparison-table-wrap {
    display: none;
  }

  .comparison-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .checkout-header {
    margin-bottom: 16px;
  }

  .secure-checkout-label {
    background: transparent;
    border: 0;
    padding: 0;
  }

  .order-card,
  .payment-workflow {
    border-radius: 14px;
    padding: 18px;
  }

  .checkout-summary {
    align-items: start;
    flex-direction: column;
    gap: 14px;
  }

  .checkout-total {
    text-align: left;
  }

  .payment-method-options {
    grid-template-columns: 1fr;
  }

  .order-features {
    grid-template-columns: 1fr;
  }

  .payment-intro h1 {
    font-size: 1.7rem;
  }

  .payment-step-card {
    padding: 20px 0 0;
  }

  .rib-box {
    align-items: stretch;
    flex-direction: column;
  }

  .rib-box span {
    font-size: 0.8rem;
    white-space: normal;
    word-break: break-all;
  }

  .rib-box button {
    justify-content: center;
    width: 100%;
  }

  .payment-panel-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .activation-note {
    max-width: none;
  }

  .submit-proof-btn {
    width: 100%;
  }

  .receipt-dropzone.selected {
    padding: 16px;
  }
}

@media (max-width: 420px) {
  .secure-checkout-label {
    font-size: 0;
  }

  .secure-checkout-label i {
    font-size: 1rem;
  }

  .order-card-heading {
    grid-template-columns: auto 1fr;
  }

  .change-plan-link {
    grid-column: 2;
  }

  .order-total strong {
    font-size: 1.3rem;
  }

  .receipt-dropzone.selected {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .selected-file-copy {
    max-width: calc(100% - 62px);
  }
}
</style>
